import { useMutation, useReactiveVar } from "@apollo/client";
import {
  Button,
  Center,
  Collapse,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { BsExclamationTriangleFill } from "react-icons/bs";
import { Link as RouterLink, Navigate, useNavigate } from "react-router-dom";
import { FormInputField } from "../../../components/formInputField/FormInputField";
import { ComponentLoading } from "../../../components/loading/ComponentLoading";
import { CREATE_PACKAGE } from "../../../graphql/operations/campaignOperations/operations";
import { userDetailsVar } from "../../../lib/cache";
import { KeywordsFile, UploadFileType } from "./KeywordsFile";
import { SegmentDetails } from "./SegmentDetails";
import { SegmentFormDataClass } from "./segmentFormSchema";

export const SegmentForm = () => {
  const currentUser = useReactiveVar(userDetailsVar);
  const navigate = useNavigate();
  const [createPackage] = useMutation(CREATE_PACKAGE);
  const [segmentFormData, setSegmentFormData] = useState(
    new SegmentFormDataClass(currentUser?.isStaff)
  );
  // const [keywordsExcFile, setKeywordsExcFile] = useState<UploadFileType>(
  //   {} as UploadFileType
  // );
  const [keywordsIncFile, setKeywordsIncFile] = useState<UploadFileType>(
    {} as UploadFileType
  );
  const { packageOwnerEmail } = segmentFormData.getUserDetailsFields();

  useEffect(() => {
    setSegmentFormData(new SegmentFormDataClass(currentUser?.isStaff));
  }, [currentUser]);

  if (!currentUser) {
    return <ComponentLoading text={"Loading page..."} />;
  }

  if (!(currentUser?.isStaff || currentUser?.agency.partner)) {
    return <Navigate to={"/"} />;
  }

  return (
    <>
      <Formik
        initialValues={segmentFormData.getDefaultValues()}
        validationSchema={segmentFormData.getYupSchema()}
        onSubmit={(values, { setSubmitting, setErrors, setStatus }) => {
          createPackage({
            variables: {
              packageDetails: {
                ...values,
                // keywordExclusionList: keywordsExcFile.keywordsExc[0],
                keywordInclusionList: keywordsIncFile.keywordsInc[0],
              },
            },
          })
            .then((res) => {
              navigate(`../${res.data.packageCreate.uniqueId}`);
            })
            .catch((err) => {
              err.graphQLErrors.forEach(
                (e: {
                  message: string;
                  extensions?: Record<string, string>;
                }) => {
                  if (e.extensions) {
                    setErrors(e.extensions);
                    setStatus("Your form contains errors.");
                  } else {
                    setStatus(e.message);
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }
                  setSubmitting(false);
                }
              );
            });
          setStatus("");
        }}
      >
        {(formik) => {
          return (
            <>
              <Collapse in={!!formik.status} animateOpacity={true}>
                <Center>
                  <HStack
                    color={"error"}
                    bg={"rgb(252, 234, 234)"}
                    p={5}
                    spacing={6}
                    mb={4}
                  >
                    <Icon as={BsExclamationTriangleFill} w={10} h={10} />
                    <Text fontSize={"16px"} fontWeight={"bold"}>
                      {formik.status}
                    </Text>
                  </HStack>
                </Center>
              </Collapse>
                <Form>
                  <Grid
                    templateColumns={"repeat(2, 1fr)"}
                    gap={2}
                    rowGap={8}
                    p={10}
                    bg={"white"}
                    borderRadius={"sm"}
                    w={"100%"}
                    height={"100%"}
                    justifyContent={"center"}
                  >
                    {currentUser?.isStaff && (
                      <>
                        <GridItem colSpan={1}>
                          <Field
                            name={"packageOwnerEmail"}
                            width={"100%"}
                          >
                            {({ field, form }: FieldProps) => (
                              <FormInputField
                                width={"100%"}
                                mt={0}
                                label={packageOwnerEmail.label}
                                fieldType={
                                  packageOwnerEmail.inputFormat as
                                    | "email"
                                    | "text"
                                    | "password"
                                }
                                isInvalid={
                                  !!(
                                    form.errors["packageOwnerEmail"] &&
                                    form.touched["packageOwnerEmail"]
                                  )
                                }
                                isRequired={packageOwnerEmail.isRequired}
                                placeholder={packageOwnerEmail.placeholder}
                                inputFieldProps={{
                                  ...field,
                                  variant: "greyInput",
                                }}
                                errorMessage={form.errors[
                                  "packageOwnerEmail"
                                ]?.toString()}
                                title={"User Details"}
                                isAdmin={true}
                              />
                            )}
                          </Field>
                        </GridItem>
                        <GridItem colSpan={1}/>
                      </>
                    )}

                    <SegmentDetails
                      fields={segmentFormData.getBasicDetails()}
                      formik={formik}
                      // minW={"50%"}
                      w={"100%"}
                      defaultValues={segmentFormData.getDefaultValues()}
                    />

                    <KeywordsFile
                      w={"100%"}
                      // keywordsExcFile={keywordsExcFile}
                      // setKeywordsExcFile={setKeywordsExcFile}
                      keywordsIncFile={keywordsIncFile}
                      setKeywordsIncFile={setKeywordsIncFile}
                    />
                    <GridItem colSpan={2}>
                      <Divider mt={5} />

                      <Flex
                        mt={5}
                        justifyContent={"flex-end"}
                        w={"100%"}
                      >
                        {formik.dirty && !formik.isSubmitting ? (
                          <Popover placement={"right-start"}>
                            <PopoverTrigger>
                              <Button variant={"whiteBoxButton"} mr={3}>
                                Cancel
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              bg={"text.dark"}
                              color={"white"}
                              _focus={{
                                boxShadow: "none",
                              }}
                            >
                              <PopoverCloseButton />
                              <PopoverHeader fontWeight={"bold"} fontSize={"18px"}>
                                Form Partially Filled
                              </PopoverHeader>
                              <PopoverBody>
                                Are you sure you want to discard the data entered so
                                far?
                              </PopoverBody>
                              <PopoverFooter d={"flex"} justifyContent={"flex-end"}>
                                <Button
                                  onClick={() => navigate("../")}
                                  variant={"primary"}
                                  bg={"red.500"}
                                  color={"white"}
                                  size={"sm"}
                                >
                                  Yes
                                </Button>
                              </PopoverFooter>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <Button
                            as={RouterLink}
                            mr={3}
                            to={"../"}
                            variant={"whiteBoxButton"}
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          type={"submit"}
                          variant={"blueButton"}
                          disabled={
                            !(
                              formik.isValid &&
                              formik.dirty &&
                              // (keywordsExcFile?.keywordsExc
                              //   ? keywordsExcFile.keywordsExc.length !== 0
                              //   : false) &&
                              (keywordsIncFile?.keywordsInc
                                ? keywordsIncFile.keywordsInc.length !== 0
                                : false)
                            )
                          }
                          isLoading={formik.isSubmitting}
                          onClick={() => {
                            formik.submitForm();
                          }}
                        >
                          Create Segment
                        </Button>
                      </Flex>

                    </GridItem>
                  </Grid>
                </Form>
            </>
          );
        }}
      </Formik>
    </>
  );
};
